import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
// Hooks
import { useAuthContext } from "../hooks/useAuthContext";
import { useLogout } from "../hooks/useLogout";
import { useCollection } from "../hooks/useCollection";
// Styles
import "../styles/components/Navbar.css";

const Navbar = () => {
  const { user } = useAuthContext();
  const { logout } = useLogout();
  const { documents: users } = useCollection("users");
  const [benomaLogo, setBenomaLogo] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Find Benoma Engineering profile/logo
  useEffect(() => {
    if (!users) return;

    const benoma = users.find((u) => u.name?.toLowerCase() === "benoma engineering");
    setBenomaLogo(benoma?.photoURL || null);
  }, [users]);

  // Navbar scroll state
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    closeMenu();
  };

  const getUserInitial = () => {
    const name = user?.displayName || user?.email || "U";
    return name.charAt(0).toUpperCase();
  };

  return (
    <>
      <nav
        className={`navbar navbar-expand-lg navbar-dark fixed-top benoma-navbar ${
          scrolled ? "navbar-scrolled" : ""
        }`}
      >
        <div className="container py-2">
          {/* BRAND */}
          <Link
            to="/"
            className="navbar-brand d-flex align-items-center gap-2 me-lg-5"
            onClick={closeMenu}
            aria-label="Benoma home"
          >
            {benomaLogo ? (
              <img src={benomaLogo} alt="Benoma" className="benoma-logo" />
            ) : (
              <span className="benoma-logo-fallback d-inline-flex align-items-center justify-content-center">
                B
              </span>
            )}
            <span className="d-flex flex-column lh-sm">
              <span className="fw-bold text-light">Benoma</span>
              <small className="benoma-brand-subtitle font-monospace">DIGITAL / ENGINEERING</small>
            </span>
          </Link>
          {/* MOBILE CONTROLS */}
          <div className="d-flex align-items-center gap-2 order-lg-3">
            {/* USER DROPDOWN */}
            {user && (
              <div className="dropdown">
                <button
                  className="btn p-1 border border-secondary rounded-pill d-flex align-items-center gap-1 benoma-profile-button"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  aria-label="Account menu"
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || "User"}
                      className="rounded-circle object-fit-cover"
                      width="34"
                      height="34"
                    />
                  ) : (
                    <span className="rounded-circle bg-danger text-white d-inline-flex align-items-center justify-content-center fw-bold user-initial">
                      {getUserInitial()}
                    </span>
                  )}
                  <i className="bi bi-chevron-down small text-secondary d-none d-sm-inline me-1" />
                </button>
                <ul className="dropdown-menu dropdown-menu-end dropdown-menu-dark benoma-dropdown shadow">
                  <li className="px-3 py-2">
                    <small className="text-secondary d-block text-uppercase benoma-dropdown-label">
                      Signed in as
                    </small>
                    <strong className="small d-block text-truncate">
                      {user.displayName || user.email}
                    </strong>
                    {user.displayName && user.email && (
                      <small className="text-secondary d-block text-truncate">{user.email}</small>
                    )}
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <Link
                      to="/create-project"
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={closeMenu}
                    >
                      <span>Create Project</span>
                      <i className="bi bi-plus-lg" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/edit-profile"
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={closeMenu}
                    >
                      <span>Edit Profile</span>
                      <i className="bi bi-person" />
                    </Link>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button
                      type="button"
                      className="dropdown-item text-danger d-flex align-items-center justify-content-between"
                      onClick={handleLogout}
                    >
                      <span>Logout</span>
                      <i className="bi bi-box-arrow-right" />
                    </button>
                  </li>
                </ul>
              </div>
            )}
            {/* MOBILE TOGGLER */}
            <button
              className={`navbar-toggler border-0 shadow-none ${isOpen ? "open" : ""}`}
              type="button"
              onClick={() => setIsOpen((prev) => !prev)}
              aria-expanded={isOpen}
              aria-label="Toggle navigation"
            >
              <span className="benoma-toggler">
                <span />
                <span />
              </span>
            </button>
          </div>
          {/* DESKTOP NAV */}
          <div className="collapse navbar-collapse order-lg-2">
            <ul className="navbar-nav ms-auto align-items-lg-center gap-lg-2">
              <li className="nav-item">
                <NavLink
                  to="/projects"
                  className={({ isActive }) =>
                    `nav-link benoma-nav-link px-lg-3 ${isActive ? "active" : ""}`
                  }
                >
                  Projects
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/contact"
                  className={({ isActive }) =>
                    `nav-link benoma-nav-link px-lg-3 ${isActive ? "active" : ""}`
                  }
                >
                  Contact
                </NavLink>
              </li>
              <li className="nav-item ms-lg-3">
                <Link
                  to="/contact"
                  className="btn btn-danger btn-sm px-3 py-2 d-inline-flex align-items-center gap-2"
                >
                  Start a Project
                  <span>↗</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      {/* MOBILE MENU */}
      <div className={`benoma-mobile-menu d-lg-none ${isOpen ? "show" : ""}`}>
        <div className="container h-100">
          <div className="d-flex flex-column h-100 pt-5 pb-4">
            <small className="text-secondary fw-bold benoma-menu-label">NAVIGATION</small>
            <nav className="mt-3 border-top border-secondary-subtle">
              <NavLink
                to="/projects"
                onClick={closeMenu}
                className={({ isActive }) =>
                  `mobile-nav-link d-flex align-items-center gap-3 py-4 border-bottom border-secondary-subtle text-decoration-none ${
                    isActive ? "active" : ""
                  }`
                }
              >
                <small className="font-monospace text-secondary">01</small>
                <span className="display-6 fw-bold flex-grow-1">Projects</span>
                <span>↗</span>
              </NavLink>
              <NavLink
                to="/contact"
                onClick={closeMenu}
                className={({ isActive }) =>
                  `mobile-nav-link d-flex align-items-center gap-3 py-4 border-bottom border-secondary-subtle text-decoration-none ${
                    isActive ? "active" : ""
                  }`
                }
              >
                <small className="font-monospace text-secondary">02</small>
                <span className="display-6 fw-bold flex-grow-1">Contact</span>
                <span>↗</span>
              </NavLink>
            </nav>
            {/* ACCOUNT */}
            {user && (
              <div className="mt-4">
                <small className="text-secondary fw-bold benoma-menu-label">ACCOUNT</small>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Link
                    to="/create-project"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={closeMenu}
                  >
                    Create Project
                  </Link>
                  <Link
                    to="/edit-profile"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={closeMenu}
                  >
                    Edit Profile
                  </Link>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
            {/* MOBILE CTA */}
            <div className="mt-auto border-top border-secondary-subtle pt-4">
              <small className="text-secondary fw-bold benoma-menu-label d-block mb-2">
                HAVE SOMETHING IN MIND?
              </small>
              <Link
                to="/contact"
                className="fs-4 fw-bold text-light text-decoration-none d-inline-flex align-items-center gap-2"
                onClick={closeMenu}
              >
                Let's build it
                <span className="text-danger">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
